import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from 'use-intl';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, CreditCard, Zap, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { ErpConnectionModal } from "@/components/erp/ErpConnectionModal";
import { isErpConnectionEnabled } from "@/config/features";

interface SetupModalProps {
  isOpen: boolean;
  erpConnectionCompleted: boolean;
  bankConnectionCompleted: boolean;
  isLoading?: boolean;
  onClose: () => void;
  onSkipERP: () => void;
}

export function SetupModal({ isOpen, erpConnectionCompleted, bankConnectionCompleted, isLoading = false, onClose, onSkipERP }: SetupModalProps) {
  const t = useTranslations('consultantOnboarding.setup');
  const navigate = useNavigate();
  const erpConnectionEnabled = isErpConnectionEnabled();
  const [isErpModalOpen, setIsErpModalOpen] = useState(false);

  const handleConnectBank = () => {
    navigate('/bank', { state: { isOnboarding: true } });
    onClose();
  };

  const handleConnectERP = () => {
    setIsErpModalOpen(true);
  };

  const handleErpModalClose = () => {
    setIsErpModalOpen(false);
  };

  const handleErpSuccess = () => {
    setIsErpModalOpen(false);
    // The onboarding status will be updated by the callback handler
    // This modal will close automatically when erpConnectionCompleted becomes true
  };

  const handleSkipERP = () => {
    onSkipERP();
    onClose();
  };

  return (
    <>
    <Dialog
      open={isOpen}
      onOpenChange={onClose}
    >
      <DialogContent 
        className="sm:max-w-lg"
        hideCloseButton={false}
        onEscapeKeyDown={undefined}
        onPointerDownOutside={undefined}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {t('title')}
          </DialogTitle>
          <DialogDescription>
            {t('description')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Mandatory Step - Connect Bank Account */}
          <div className="flex items-start space-x-4 p-4 rounded-lg border">
            <div className={cn(
              "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-1",
              isLoading ? "bg-gray-400" : bankConnectionCompleted ? "bg-green-500" : "bg-red-500"
            )}>
              {isLoading ? (
                <Loader2 className="w-4 h-4 text-white animate-spin" />
              ) : bankConnectionCompleted ? (
                <Check className="w-4 h-4 text-white" />
              ) : (
                <CreditCard className="w-4 h-4 text-white" />
              )}
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-sm">
                {t('bank.title')} {!isLoading && !bankConnectionCompleted && <span className="text-red-500">{t('bank.required')}</span>}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {isLoading ? t('checking') : t('bank.body')}
              </p>
              {!isLoading && !bankConnectionCompleted && (
                <Button
                  onClick={handleConnectBank}
                  className="mt-3"
                  size="sm"
                >
                  {t('bank.action')}
                </Button>
              )}
            </div>
          </div>

          {/* Recommended Step - Connect ERP */}
          {erpConnectionEnabled && (
            <div className="flex items-start space-x-4 p-4 rounded-lg border">
              <div className={cn(
                "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-1",
                isLoading ? "bg-gray-400" : erpConnectionCompleted ? "bg-green-500" : "bg-blue-500"
              )}>
                {isLoading ? (
                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                ) : erpConnectionCompleted ? (
                  <Check className="w-4 h-4 text-white" />
                ) : (
                  <Zap className="w-4 h-4 text-white" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm">
                  {t('erp.title')} <span className="text-muted-foreground">{t('erp.recommended')}</span>
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {isLoading ? t('checking') : t('erp.body')}
                </p>
                {!isLoading && !erpConnectionCompleted && (
                  <div className="flex space-x-2 mt-3">
                    <Button
                      onClick={handleConnectERP}
                      variant="outline"
                      size="sm"
                    >
                      {t('erp.action')}
                    </Button>
                    <Button
                      onClick={handleSkipERP}
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground"
                    >
                      {t('erp.skip')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {!isLoading && bankConnectionCompleted && (
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={onClose}>
              {t('complete')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {erpConnectionEnabled && (
      <ErpConnectionModal
        isOpen={isErpModalOpen}
        onClose={handleErpModalClose}
        onSuccess={handleErpSuccess}
      />
    )}
    </>
  );
}